import type {
  ApplyRowResult,
  RelabelPreview,
} from "../../../api/profiles/websiteRelabelService.js";
import type { ManifestRow } from "./parseManifest.js";
import { resolveContent, resolveName } from "./rowDisplay.js";

/**
 * Builds the rollback manifests for the website relabel tool (SOFTSUPP-532).
 *
 * Both flavours are themselves valid upload manifests: current and target are
 * swapped so that uploading one relabels every covered row back to what the
 * database held before the run.
 *
 * - `buildRollbackManifest` is the authoritative, post-apply file. `current`
 *   comes from the run's TARGET values (what is in the DB now) and `target`
 *   from the `previous` state the apply response captured — not from the
 *   uploaded file's `current` columns, which may have been stale.
 * - `buildPlannedRollbackManifest` is the pre-apply file, so a tab that dies
 *   mid-run still leaves an undo behind. See its own note for why handing out
 *   a rollback for a run that has not happened yet is safe.
 */

const HEADERS = [
  "table",
  "id",
  "current_website",
  "current_lang",
  "target_website",
  "target_lang",
  "source_site",
  "source_post_id",
  "detected_lang",
  "anomaly",
  "status_hint",
] as const;

/**
 * The planned rollback carries two extra columns so a reviewer can recognise a
 * row without re-running the tool. They round-trip: parseManifest reads `name`
 * and `excerpt` back, so re-uploading this file shows the same names again —
 * which is the only way firm rows get one, the API sending none for them.
 */
const PLANNED_HEADERS = [...HEADERS, "name", "excerpt"] as const;

/**
 * Leading characters that make Excel and Sheets treat a cell as a formula.
 * The passthrough columns (source_site, anomaly, name, excerpt, ...) come
 * straight from an uploaded file, so they are untrusted as far as this export
 * is concerned — and the round-trip above means a defused cell can be uploaded
 * and written out again, so this must be idempotent-safe rather than one-shot.
 */
const FORMULA_TRIGGERS = ["=", "+", "-", "@", "\t", "\r"];

/**
 * Quotes a field only when it needs it, and doubles any embedded quote —
 * anomaly notes carry commas and quotes.
 *
 * A field starting with a formula trigger is prefixed with a single quote so
 * the spreadsheet reads it as text. Quoting alone does not prevent this: Excel
 * strips the quotes and evaluates what is inside.
 */
function csvField(value: string | null): string {
  let text = value === null ? "" : value;
  if (FORMULA_TRIGGERS.some(trigger => text.startsWith(trigger))) {
    text = `'${text}`;
  }
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/**
 * @param results Apply results, in the SAME ORDER as `rows` — position is the
 *   only reliable join, since a manifest may contain duplicate ids.
 * @param rows The parsed rows that were sent to apply, matching `results` by index.
 */
export function buildRollbackManifest(results: ApplyRowResult[], rows: ManifestRow[]): string {
  const lines = [HEADERS.join(",")];
  results.forEach((result, index) => {
    const row = rows[index];
    // Only rows the API actually wrote can be rolled back. `previous` is
    // guaranteed on status "ok", but the guard keeps a malformed response from
    // producing a rollback row that would relabel to nothing.
    if (!row || result.status !== "ok" || !result.previous) {
      return;
    }
    lines.push(
      [
        row.table,
        row.id,
        // What the DB holds now: the values this run just applied.
        row.targetWebsite,
        row.targetLanguage,
        // Where the rollback takes it: the pre-update state from the API.
        result.previous.website,
        result.previous.languageCode,
        row.sourceSite,
        row.sourcePostId,
        row.detectedLang,
        row.anomaly,
        "rollback",
      ]
        .map(csvField)
        .join(","),
    );
  });
  return `${lines.join("\n")}\n`;
}

/** One validated row the reviewer has selected for the run that is about to happen. */
export type PlannedRollbackEntry = {
  row: ManifestRow;
  /** Absent when the API sent no content preview for the row. */
  preview?: RelabelPreview;
};

/**
 * Builds the rollback manifest for a run that has NOT happened yet, from the
 * rows the reviewer currently has selected.
 *
 * The swap is the same as the post-apply file's: `current_*` is what this run
 * is about to write, `target_*` is what the database holds right now.
 *
 * Handing this out before applying is safe even though the run may diverge
 * from the plan. Uploading a rollback manifest re-validates its `current_*`
 * columns against live database state, so any row that ultimately was NOT
 * written — deselected before the run, failed, or skipped — still holds its
 * original values and comes back as `stale_current`, which is never applied. A
 * pre-generated rollback can therefore only ever undo rows the run actually
 * changed; it can never revert something that never happened.
 *
 * It is still the weaker of the two files: `target_*` comes from the uploaded
 * manifest's `current_*` columns rather than from the `previous` state the API
 * captured, so the post-apply manifest remains the authoritative one.
 */
export function buildPlannedRollbackManifest(entries: PlannedRollbackEntry[]): string {
  const lines = [PLANNED_HEADERS.join(",")];
  for (const { row, preview } of entries) {
    // Same precedence the table displays by, so the file says what the reviewer
    // read — including the manifest's own name for firm rows, which the API
    // never describes. Every value goes through csvField, uploaded or not.
    const content = resolveContent(row, preview);
    lines.push(
      [
        row.table,
        row.id,
        // What the DB will hold once this run lands: the values it is about to write.
        row.targetWebsite,
        row.targetLanguage,
        // Where the rollback takes it: what the DB holds now, per the manifest.
        row.currentWebsite,
        row.currentLanguage,
        row.sourceSite,
        row.sourcePostId,
        row.detectedLang,
        row.anomaly,
        "rollback_planned",
        resolveName(row, preview) ?? "",
        content?.excerpt ?? "",
      ]
        .map(csvField)
        .join(","),
    );
  }
  return `${lines.join("\n")}\n`;
}

/** Shared `2026-08-06-10-20-30` filename stamp, so both manifests sort together. */
function filenameStamp(now: Date): string {
  return now.toISOString().slice(0, 19).replace(/[:T]/g, "-");
}

export function rollbackFilename(now: Date): string {
  return `website-relabel-rollback-${filenameStamp(now)}.csv`;
}

/**
 * Deliberately unlike `rollbackFilename`: the planned and the authoritative
 * rollback end up in the same downloads folder, and uploading the wrong one is
 * the mistake this name exists to prevent.
 */
export function plannedRollbackFilename(now: Date): string {
  return `website-relabel-rollback-planned-${filenameStamp(now)}.csv`;
}

function downloadCsv(csv: string, filename: string): void {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  // Deferred: revoking in the same tick can cancel the download before it starts.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/**
 * Builds the post-apply rollback manifest and hands it to the browser.
 *
 * @param at Stamps the filename — pass the moment the run finished, so two
 *   runs in one session cannot produce identically named files.
 */
export function downloadRollbackManifest(
  results: ApplyRowResult[],
  rows: ManifestRow[],
  at: Date,
): void {
  downloadCsv(buildRollbackManifest(results, rows), rollbackFilename(at));
}

/** Builds the pre-apply rollback manifest and hands it to the browser. */
export function downloadPlannedRollbackManifest(entries: PlannedRollbackEntry[], at: Date): void {
  downloadCsv(buildPlannedRollbackManifest(entries), plannedRollbackFilename(at));
}
